import { useI18n } from '../lib/i18n';
import type { AnswerValue, FieldKind } from '@shared/forms';

/**
 * One intake-form question, rendered as whatever kind it is.
 *
 * Shared by the customer's secure-link form page (0041) and the service
 * request card on the booking page (0053), so a business's questions
 * look and behave the same wherever a customer meets them. Every
 * control is a real input with a real label; a group of choices is a
 * fieldset with a legend; a wrong answer sets aria-invalid and points at
 * its own message.
 */

export interface FormField {
  id: string;
  section_id: string | null;
  kind: FieldKind;
  label: string;
  help_text: string | null;
  required: boolean;
  position: number;
  options: string[];
  config: Record<string, unknown>;
}

export interface FormSection { id: string; title: string; description: string | null; position: number }

export function FormQuestion({ field, value, issue, onChange, onFile, file }: {
  field: FormField;
  value: AnswerValue;
  issue?: string;
  onChange: (id: string, value: AnswerValue) => void;
  /** Absent where a file cannot be attached (the request card): the field says so. */
  onFile?: (field: FormField, file: File) => void;
  file?: { name: string } | 'uploading';
}) {
  const { t } = useI18n();
  const id = `f-${field.id}`;
  const described = [field.help_text ? `${id}-help` : null, issue ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;

  if (field.kind === 'heading') return <h3 className="form-field-heading">{field.label}</h3>;
  if (field.kind === 'info') return <p className="muted form-field-info">{field.label}</p>;

  const label = (
    <label htmlFor={id}>
      {field.label} <span className="muted small">{field.required ? t('form.required') : t('form.optional')}</span>
    </label>
  );
  const help = field.help_text ? <p className="muted small" id={`${id}-help`}>{field.help_text}</p> : null;
  const error = issue ? <p className="error" id={`${id}-error`}>{issue}</p> : null;
  const common = {
    id,
    required: field.required,
    'aria-invalid': issue ? true : undefined,
    'aria-describedby': described,
  } as const;

  // A group of controls is a fieldset with a legend, so the question is
  // announced once for the whole group rather than not at all.
  if (field.kind === 'yes_no' || field.kind === 'single_choice' || field.kind === 'multi_choice') {
    const options = field.kind === 'yes_no'
      ? [{ value: 'yes', label: t('form.yes') }, { value: 'no', label: t('form.no') }]
      : field.options.map((o) => ({ value: o, label: o }));
    const multiple = field.kind === 'multi_choice';
    const chosen = multiple ? (Array.isArray(value) ? value : []) : value;
    return (
      <fieldset className="field" aria-invalid={issue ? true : undefined} aria-describedby={described}>
        <legend>
          {field.label} <span className="muted small">{field.required ? t('form.required') : t('form.optional')}</span>
        </legend>
        {help}
        <p className="muted small">{multiple ? t('form.choose_any') : t('form.choose')}</p>
        {options.map((option) => (
          <label key={option.value} className="choice">
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={id}
              value={option.value}
              checked={multiple ? (chosen as string[]).includes(option.value) : chosen === option.value}
              onChange={(e) => {
                if (!multiple) return onChange(field.id, option.value);
                const next = new Set(chosen as string[]);
                if (e.target.checked) next.add(option.value); else next.delete(option.value);
                onChange(field.id, [...next]);
              }}
            />
            {option.label}
          </label>
        ))}
        {error}
      </fieldset>
    );
  }

  if (field.kind === 'acknowledgment') {
    return (
      <div className="field">
        <label className="choice" htmlFor={id}>
          <input type="checkbox" {...common} checked={value === true}
                 onChange={(e) => onChange(field.id, e.target.checked)} />
          <span>{field.label} <span className="muted small">{field.required ? t('form.required') : t('form.optional')}</span></span>
        </label>
        {help}
        {error}
      </div>
    );
  }

  if (field.kind === 'file') {
    if (!onFile) {
      // Nothing to upload against yet; the question is shown so the
      // customer knows the business may ask for it.
      return (
        <div className="field">
          {label}
          {help}
          <p className="muted small">{t('form.attach_hint')}</p>
        </div>
      );
    }
    return (
      <div className="field">
        {label}
        {help}
        <input type="file" {...common} accept="image/jpeg,image/png,image/webp,application/pdf"
               onChange={(e) => { const chosen = e.target.files?.[0]; if (chosen) onFile(field, chosen); }} />
        <p className="muted small">{t('form.attach_hint')}</p>
        {file === 'uploading' && <p className="muted small">{t('form.attaching')}</p>}
        {file && file !== 'uploading' && <p className="muted small">{t('form.attached', { name: file.name })}</p>}
        {error}
      </div>
    );
  }

  const input = field.kind === 'long_text' || field.kind === 'address'
    ? <textarea {...common} rows={field.kind === 'address' ? 3 : 5} value={String(value ?? '')}
                onChange={(e) => onChange(field.id, e.target.value)} />
    : <input
        {...common}
        type={field.kind === 'email' ? 'email' : field.kind === 'phone' ? 'tel'
          : field.kind === 'date' ? 'date' : field.kind === 'time' ? 'time' : 'text'}
        inputMode={field.kind === 'phone' ? 'tel' : undefined}
        autoComplete={field.kind === 'email' ? 'email' : field.kind === 'phone' ? 'tel' : undefined}
        value={String(value ?? '')}
        onChange={(e) => onChange(field.id, e.target.value)}
      />;

  return (
    <div className="field">
      {field.kind === 'signature' || field.kind === 'initials'
        ? <label htmlFor={id}>{field.label} <span className="muted small">{t(field.kind === 'initials' ? 'form.initials_label' : 'form.signature_label')}</span></label>
        : label}
      {help}
      {input}
      {error}
    </div>
  );
}
